import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Plotly from 'plotly.js-dist-min';
import { Box, css, Checkbox, Select, Stack, TextField } from '@mui/material';

// import { UseLayerDashboardStore } from '../stores/useLayerDashboardStore';
import { sortAttributesToPlot } from '../utils/attributesUtil';
import { TitleChart } from './TitleChart';
import { useLayerDashboardTabStore } from '../context/LayerDashboardContext';

const styles = css({
    height: '100%',
    '.container-chart': {
        height: '100%',
        width: '100%'
    }
});

const boxes = css({
    marginRight: 5,
    marginBottom: 5
});

const checkBox = css({
    alignSelf: 'center',
    alignItems: 'center',
    marginTop: '16px'
});

const stats = css({
    borderRadius: 5,
    padding: 10,
    backgroundColor: 'rgb(4, 31, 60, 0.6)',
    color: 'white'
});

export const Histogramplot = () => {
    const { selectedData, displayableData, attributes, setSelectedData } = useLayerDashboardTabStore(state => ({
        displayableData: state.displayableData,
        attributes: state.attributes,
        setSelectedData: state.setSelectedData,
        selectedData: state.selectedData
    }));

    let histogramplotId = 'histogramplot-' + Math.random().toString(36).substring(2);

    const changedBy = useRef<boolean>(false);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [histogramData, setHistogramData] = useState<any>([]);
    const [binSize, setBinSize] = useState(1);
    const [startBin, setStartBin] = useState(0);
    const [minValue, setMinValue] = useState(0);
    const [maxValue, setMaxValue] = useState(0);
    const [countValues, setCountValues] = useState(0);
    const [totalBins, setTotalBins] = useState(0);
    const [histogramField, setHistogramField] = useState<string>('porosidade_perc_ncs');
    const [histogramFieldLabel, setHistogramFieldLabel] = useState<string | undefined>('porosidade_perc_ncs');
    const [histogramAxisType, setHistogramAxisType] = useState<number | undefined>(0);
    const [percentiles, setPercentiles] = useState<number[]>([]);
    const [percentileCheck, setPercentileCheck] = useState<boolean>(false);
    const [average, setAverage] = useState<number>(0);

    const updateBySelectedData = useCallback(() => {
        if (selectedData && selectedData.length > 0) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const ensuredData = displayableData.filter((el: any) => typeof (el[histogramField]) === 'number');
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            let pointNumbers: any = [];
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const selectedXValues: any = [];
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ensuredData.forEach((el: any, idx: number) => {
                if (selectedData.filter(z => z.dashboardpointidentifier === el.dashboardpointidentifier).length > 0) {
                    //pointNumbers.push(idx);
                    selectedXValues.push(el[histogramField]);
                }
            });
            //eventData.points[1].data.xbins
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            selectedXValues.forEach((xValue: number) => {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                pointNumbers.push(parseInt((xValue / binSize).toString()) - startBin - 1);
            });
            let uniqueValues = new Set(pointNumbers);
            pointNumbers = [];
            uniqueValues.forEach((x) => pointNumbers.push(x));

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            let opacities = [];
            for (let i = 0; i < totalBins; i++) {
                opacities.push(0.35);
            }

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            pointNumbers.forEach(function (pointNumber: any) {
                opacities[pointNumber] = 1;
            });

            let update = { marker: { opacity: opacities } };
            Plotly.restyle(histogramplotId, update, [0]);

            console.log('INSIDE SELECTED DATA', update);
        }
    }, [selectedData, JSON.stringify(displayableData), binSize, startBin, histogramField, totalBins]);

    const GetAxisType = () => {
        switch (histogramAxisType) {
            case 1:
                return 'log';
            case 0:
            default:
                return 'linear';
        }
    };

    useEffect(() => {
        setHistogramData(displayableData.map(x => ({
            x: x[histogramField],
            dashboardpointidentifier: x.dashboardpointidentifier,
        })));
        setHistogramFieldLabel(attributes.find(x => x.Name === histogramField)?.Label);
        setHistogramAxisType(attributes.find(x => x.Name === histogramField)?.AxisType);

        changedBy.current = true;
    }, [JSON.stringify(displayableData), histogramField, binSize]);

    useEffect(() => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const ensuredData = histogramData.filter((el: any) => typeof (el.x) === 'number');
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const allXValues = ensuredData.map((el: any) => el.x!);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const startBin = parseInt(allXValues.sort((a: number, b: number) => a - b)[0]) - binSize;
        const lastBin = parseInt(allXValues.sort((a: number, b: number) => b - a)[0]) + binSize;
        const countValues = Array.from(new Set(allXValues)).length;
        setTotalBins(lastBin - startBin + 1);
        setStartBin(startBin);
        setMinValue(allXValues.sort((a: number, b: number) => a - b)[0]);
        setMaxValue(allXValues.sort((a: number, b: number) => b - a)[0]);
        setCountValues(countValues);

        let percentileValues = getPercentile(allXValues);

        let average = getAverage(allXValues);

        const trace1: Plotly.Data = {
            x: allXValues,
            mode: 'markers',
            type: 'histogram',
            xbins: {
                start: startBin,
                end: lastBin,
                size: binSize
            }
        };

        const data = [trace1];

        const render = async () => {
            const plotDiv = await Plotly.newPlot(histogramplotId, data, {
                bargap: 0.5,
                bargroupgap: 0.1,
                barmode: 'overlay',
                xaxis: {
                    type: GetAxisType(),
                    title: {
                        text: histogramFieldLabel,
                        font: { size: 18 }
                    },
                    automargin: true,
                },
                yaxis: {
                    automargin: true,
                    title: {
                        text: 'Count',
                        font: { size: 18 }
                    }
                },
                autosize: true,
                margin: {
                    l: 25,
                    r: 25,
                    b: 25,
                    t: 25,
                    pad: 5
                },
                ...(percentileCheck && {
                    shapes: percentileValues.map((value, idx) => ({
                        type: 'line',
                        x0: value,
                        x1: value,
                        y0: 0,
                        y1: 1000,
                        yref: 'paper',
                        line: {
                            color: ['red', 'green', 'blue'][idx],
                            width: 2,
                            dash: 'dot'
                        }
                    })),
                    annotations: percentileValues.map((value, idx) => ({
                        x: value,
                        y: 1.05,
                        xref: 'x',
                        yref: 'paper',
                        text: [`P10(${percentileValues[0].toFixed(2)})`, `P50(${percentileValues[1].toFixed(2)})`, `P90(${percentileValues[2].toFixed(2)})`][idx],
                        showarrow: false,
                        font: {
                            color: 'white',
                            size: 12,
                            family: 'Arial'
                        },
                        align: 'center',
                        bgcolor: ['red', 'green', 'blue'][idx],
                        bordercolor: '#333',
                        borderwidth: 1,
                        borderpad: 4,
                        opacity: 0.9,
                    }))
                })
            }, {
                autosizable: true,
                responsive: true,
                displayModeBar: true,
                displaylogo: false,
                modeBarButtonsToRemove: ['zoomIn2d', 'zoomOut2d', 'autoScale2d']
            });
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            plotDiv?.on('plotly_selected', function (eventData: any) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const ensuredData = displayableData.filter((el: any) => typeof (el[histogramField]) === 'number');
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                let locatedData: any = [];
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                let binRanges: any = [];
                for (let x = 0; x < eventData.points.length; x++) {
                    const point = eventData.points[x];
                    const binMin = point.x - (binSize / 2);
                    const binMax = point.x + (binSize / 2);
                    binRanges.push([binMin, binMax]);
                }
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                locatedData = ensuredData.filter((el: any) => binRanges.filter((x: any) => el[histogramField] >= x[0] && el[histogramField] < x[1]).length > 0);
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                setSelectedData(locatedData);
            });

            updateBySelectedData();
        };
        render();
    }, [histogramData]);

    const getPercentile = (allXValues: number[]) => {
        let ordenatedValues = allXValues.sort((a: number, b: number) => a - b);

        let p10 = calcPercentile(ordenatedValues, 10);
        let p50 = calcPercentile(ordenatedValues, 50);
        let p90 = calcPercentile(ordenatedValues, 90);

        setPercentiles([p10, p50, p90]);

        return [p10, p50, p90];
    };

    const calcPercentile = (allXValues: number[], percentile: number) => {
        let position = (percentile / 100) * (allXValues.length - 1);
        let base = Math.floor(position);
        let rest = position - base;

        if (allXValues[base + 1] !== undefined) {
            return allXValues[base] + rest * (allXValues[base + 1] - allXValues[base]);
        }
        else {
            return allXValues[base];
        }
    };

    const getAverage = (allXValues: number[]) => {
        let sum = 0;
        allXValues.map(x => sum += x);

        let average = sum / allXValues.length;

        setAverage(average);

        return average;
    };

    useEffect(() => {
        if (percentileCheck) {
            Plotly.relayout(histogramplotId, {
                shapes: percentiles.map((value, idx) => ({
                    type: 'line',
                    x0: value,
                    x1: value,
                    y0: 0,
                    y1: 1000,
                    yref: 'paper',
                    line: {
                        color: ['red', 'green', 'blue'][idx],
                        width: 2,
                        dash: 'dot'
                    }
                })),
                annotations: percentiles.map((value, idx) => ({
                    x: value,
                    y: 1.05,
                    xref: 'x',
                    yref: 'paper',
                    text: [`P10(${percentiles[0].toFixed(2)})`, `P50(${percentiles[1].toFixed(2)})`, `P90(${percentiles[2].toFixed(2)})`][idx],
                    showarrow: false,
                    font: {
                        color: 'white',
                        size: 12,
                        family: 'Arial'
                    },
                    align: 'center',
                    bgcolor: ['red', 'green', 'blue'][idx],
                    bordercolor: '#333',
                    borderwidth: 1,
                    borderpad: 4,
                    opacity: 0.9,
                }))
            });
        }
        else {
            Plotly.relayout(histogramplotId, {
                shapes: [],
                annotations: []
            });
        }
    }, [percentileCheck]);

    useEffect(() => {
        if (changedBy.current) {
            changedBy.current = false;
            return;
        }
        updateBySelectedData();
        console.log('useeffect second');
    }, [JSON.stringify(selectedData), updateBySelectedData]);

    const attributesPlotSorted = useMemo(() => {
        return sortAttributesToPlot(attributes);
    }, [JSON.stringify(attributes)]);

    return (
        <Stack direction='column' css={styles}>
            <TitleChart title='Histogram' maximizeOperation={'histogram'} />
            <Stack direction='row'>
                <Stack direction='column'>
                    <Box css={boxes}>
                        Field
                    </Box>
                    <Box css={boxes}>
                        <Select native
                            size='small'
                            value={histogramField}
                            onChange={(evt) => setHistogramField(evt.target.value)}
                        >
                            {attributesPlotSorted.map(x => <option key={'histogram-attr-x-' + x.Id} value={x.Name}>{x.Label}</option>)}
                        </Select>
                    </Box>
                </Stack>
                <Stack direction='column'>
                    <Box css={boxes}>
                        Bin Size
                    </Box>
                    <Box css={boxes}>
                        <TextField size='small' defaultValue={binSize} type='number' onChange={(evt) => setBinSize(parseFloat(evt.target.value) < 0 ? 1 : parseFloat(evt.target.value))}></TextField>
                    </Box>
                </Stack>
                <Stack direction='row' css={checkBox}>
                    <Box css={boxes}>
                        Percentile
                    </Box>
                    <Box css={boxes}>
                        <Checkbox checked={percentileCheck} onChange={() => setPercentileCheck(!percentileCheck)} />
                    </Box>
                </Stack>
            </Stack>
            <Stack direction={'row'} css={stats} justifyContent={'space-between'}>
                <Box>
                    <strong>Count Values</strong>: {countValues}
                </Box>
                <Box>
                    <strong>Average</strong>: {average.toFixed(2)}
                </Box>
                <Box>
                    <strong>Min Value</strong>: {minValue}
                </Box>
                <Box>
                    <strong>Max Value</strong>: {maxValue}
                </Box>
            </Stack>
            <Stack direction='row' flexGrow={1}>
                <div id={histogramplotId} className='container-chart'></div>
            </Stack>
        </Stack>
    );
};
